/**
 * GooglePreferredSourceButton
 *
 * Links to Google's reader-controlled source preferences tool. A preferred
 * source is a personal preference, not an independent trust certification or
 * a guarantee of rankings or AI citations. The compact option is text-only.
 * https://developers.google.com/search/docs/appearance/preferred-sources
 */

const DEEPLINK = "https://google.com/preferences/source?q=integrated.social";

// Official Google badge assets uploaded to CDN
const BADGE_DARK = "/media/google_preferred_source_badge_dark_en_4e66a5b1.png";
const BADGE_LIGHT = "/media/google_preferred_source_badge_light_en_d93b7a57.png";

interface GooglePreferredSourceButtonProps {
  /** Use 'dark' on dark backgrounds (default), 'light' on light backgrounds */
  variant?: "dark" | "light";
  /** Text-only option for article bylines and reading tools */
  compact?: boolean;
  /** Localizes the compact option only; the default badge stays unchanged */
  locale?: "en" | "ar";
  /** Additional CSS classes */
  className?: string;
}

export function GooglePreferredSourceButton({
  variant = "dark",
  compact = false,
  locale = "en",
  className = "",
}: GooglePreferredSourceButtonProps) {
  const badgeSrc = variant === "dark" ? BADGE_DARK : BADGE_LIGHT;
  const altText = "Add Integrated.Social as a preferred source on Google";

  if (compact) {
    const arabic = locale === "ar";
    const label = arabic ? "أضفنا كمصدر مفضّل على Google" : "Add as a preferred source";
    const title = arabic
      ? "اختر Integrated.Social كمصدر مفضّل على Google"
      : "Choose Integrated.Social as a preferred source on Google";
    const accessibleLabel = arabic
      ? `${label}: Integrated.Social (يفتح في علامة تبويب جديدة)`
      : `${label}: Integrated.Social on Google (opens in a new tab)`;

    return (
      <a
        href={DEEPLINK}
        target="_blank"
        rel="noopener noreferrer"
        lang={locale}
        dir={arabic ? "rtl" : "ltr"}
        className={`inline-flex min-h-10 min-w-6 max-w-full items-center justify-center rounded-md border px-3 py-2 text-center text-xs font-medium leading-5 whitespace-normal underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current ${variant === "dark" ? "border-white/25 text-slate-100 hover:bg-white/10" : "border-slate-500 text-slate-800 hover:bg-slate-100"} ${className}`}
        aria-label={accessibleLabel}
        title={title}
      >
        {label}
      </a>
    );
  }

  return (
    <a
      href={DEEPLINK}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center ${className}`}
      aria-label={altText}
      title="Add us as a preferred source on Google - see more of our stories in Top Stories, AI Mode and AI Overviews"
    >
      <img
        src={badgeSrc}
        alt={altText}
        width={120}
        height={40}
        style={{ display: "block", height: "40px", width: "auto", border: 0 }}
        loading="lazy"
      />
    </a>
  );
}
